import React, { useState, useRef, KeyboardEvent } from 'react';
import { Send } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useI18n } from '@/contexts/I18nContext';

const MAX_LENGTH = 2000;

interface MessageInputProps {
  onSend: (content: string) => Promise<void>;
  isSending: boolean;
}

export const MessageInput: React.FC<MessageInputProps> = ({ onSend, isSending }) => {
  const { t } = useI18n();
  const [value, setValue] = useState('');
  const [error, setError]  = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const remaining = MAX_LENGTH - value.length;
  const isOverLimit = remaining < 0;
  const isEmpty     = value.trim().length === 0;
  const canSend     = !isEmpty && !isOverLimit && !isSending;

  const handleSend = async () => {
    if (!canSend) return;
    setError(null);
    const content = value.trim();
    setValue('');
    try {
      await onSend(content);
    } catch (err: any) {
      setError(err?.message ?? t('pages.messages.send_failed'));
      setValue(content); // restore on failure
    }
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Ctrl/Cmd + Enter sends; plain Enter adds a newline
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="border-t border-white/10 px-3 pt-3 pb-3" style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}>
      {error && (
        <p className="text-red-400 text-xs mb-2 px-1">{error}</p>
      )}

      <div className="flex items-end gap-2">
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => { setValue(e.target.value); setError(null); }}
            onKeyDown={handleKeyDown}
            placeholder={t('pages.messages.write_placeholder')}
            rows={1}
            className={cn(
              'w-full resize-none bg-white/5 border rounded-xl px-4 py-3 text-sm text-white',
              'placeholder:text-white/30 focus:outline-none focus:ring-1 transition-colors',
              'max-h-32 overflow-y-auto',
              isOverLimit
                ? 'border-red-500 focus:ring-red-500'
                : 'border-white/10 focus:ring-white/20',
            )}
            style={{ fieldSizing: 'content' } as React.CSSProperties}
            disabled={isSending}
          />

          {/* Character counter — shown only when approaching the limit */}
          {remaining <= 200 && (
            <span
              className={cn(
                'absolute bottom-2 right-3 text-[10px]',
                isOverLimit ? 'text-red-400' : 'text-white/30',
              )}
            >
              {remaining}
            </span>
          )}
        </div>

        <button
          onClick={handleSend}
          disabled={!canSend}
          aria-label="Send message"
          className={cn(
            'p-3 rounded-xl transition-colors flex-shrink-0',
            canSend
              ? 'bg-red-600 hover:bg-red-500 text-white'
              : 'bg-white/5 text-white/20 cursor-not-allowed',
          )}
        >
          <Send className="w-5 h-5" />
        </button>
      </div>

      {isOverLimit && (
        <p className="text-red-400 text-xs mt-1 px-1">
          {t('pages.messages.message_too_long', { count: -remaining })}
        </p>
      )}
    </div>
  );
};
